"use client";

import { useState, useEffect } from "react";
import { useTheme } from "next-themes";
import Image from 'next/image';
import Link from 'next/link';
import { motion } from "framer-motion";

export default function HomePage() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mounted, setMounted] = useState(false);
  const { theme, setTheme, resolvedTheme } = useTheme();

  useEffect(() => {
    setMounted(true);
    const handleScroll = () => setIsScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const photoHomeSrc = mounted && resolvedTheme === "light" 
    ? "/images/photo-home.png" 
    : "/images/photo-home.png";

  const steps = [
    { id: "01", title: "Discovery", desc: "วิเคราะห์ Business Logic และแผนงานให้เฉียบคมตั้งแต่จุดเริ่มต้น" },
    { id: "02", title: "Architecture", desc: "วางโครงสร้างระบบด้วยมาตรฐานสากล เพื่อความเสถียรและยั่งยืน" },
    { id: "03", title: "Development", desc: "เขียนโค้ดด้วยความประณีต ผสมผสานเทคโนโลยีและงานศิลปะ" },
    { id: "04", title: "Scalability", desc: "ตรวจสอบและปรับแต่งเพื่อให้ระบบพร้อมเติบโตอย่างไร้ขีดจำกัด" },
  ];
  
  return (
    <div className="relative">
      <header className="relative h-screen w-full flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 z-0">
          <Image 
            src={photoHomeSrc}
            alt="Folda High-Performance Tech"
            fill
            className="object-cover object-center transition-opacity duration-700 opacity-100 dark:opacity-100"
            priority
          />
          <div className="absolute inset-0 z-10 bg-white/30 dark:bg-black/40 transition-colors duration-500" />
          <div className="absolute inset-0 z-20 bg-gradient-to-b from-transparent via-transparent to-background" />
        </div>

        <div className="relative z-30 max-w-[1440px] mx-auto px-6 flex flex-col items-center text-center">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-8 flex items-center gap-3 px-4 py-2 rounded-full border border-black/30 dark:border-white/20 bg-white/20 dark:bg-black/20 backdrop-blur-md"
          >
            <span className="h-2 w-2 rounded-full bg-fd-500 animate-pulse shadow-[0_0_10px_#fd-500]" />
            <span className="text-[12px] font-medium tracking-[0.1em] uppercase text-foreground/80">
              บริการพัฒนาซอฟต์แวร์ระดับพรีเมียม
            </span>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="space-y-4"
          >
            <h4 className="text-6xl md:text-6xl font-bold leading-[1.0] text-foreground uppercase">
              เหนือชั้นด้วยระบบ<br />
              <span className="text-fd-500">ทรงพลังด้วยเทคโนโลยี</span>
            </h4>
            <p className="text-lg md:text-2xl font-bold text-foreground/40 italic tracking-widest uppercase">
              High-Performance Tech
            </p>
          </motion.div>

          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="mt-12 flex flex-col md:flex-row gap-6"
          >
            <Link 
              href="/projects" 
              className="px-12 py-6 bg-fd-500 text-white dark:text-black rounded-2xl font-bold text-md tracking-[0.1em] transition-all hover:scale-105 hover:shadow-[0_20px_40px_-15px_rgba(253,80,0,0.4)]"
            >
              ชมผลงานของเรา
            </Link>
            <Link 
              href="/about" 
              className="px-12 py-6 border-2 border-foreground text-foreground rounded-2xl font-bold text-md tracking-[0.1em] transition-all hover:scale-105 hover:shadow-[0_20px_40px_-15px_rgba(253,80,0,0.4)]"
            >
              รู้จักเรามากขึ้น
            </Link>
          </motion.div>
        </div>
      </header>

      <section className="relative py-12 pt-5 px-6 md:px-12 max-w-7xl mx-auto overflow-hidden">
        <div className="absolute top-0 right-0 w-1/2 h-full bg-fd-500/[0.02] -skew-x-12 transform origin-top pointer-events-none" />
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          <motion.div 
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="relative group order-2 lg:order-1"
          >
            <div className="relative h-[350px] md:h-[500px] w-full overflow-hidden rounded-[2rem] shadow-2xl">
              <Image 
                src="/images/photo-home-code.png" 
                alt="Engineering Code"
                fill
                className="object-cover transition-transform duration-700 group-hover:scale-110"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-60" />
            </div>

            <div className="absolute bottom-8 left-8 bg-white/10 backdrop-blur-md border border-white/20 p-4 rounded-2xl hidden md:block">
              <p className="text-fd-500 font-black text-xl">100%</p>
              <p className="text-[12px] text-white/60 uppercase tracking-widest font-bold">Reliability</p>
            </div>
          </motion.div>

          <motion.div 
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="relative z-10 order-1 lg:order-2"
          >
            <div className="space-y-6">
              <div className="space-y-2">
                <span className="text-fd-500 font-bold mb-4 uppercase tracking-[0.3em] text-[14px] italic block">
                  Engineering Excellence
                </span>
                <h2 className="text-4xl md:text-6xl font-bold leading-[1.2] text-foreground uppercase">
                  ระบบที่ออกแบบเพื่อ <br />
                  <span className="text-fd-500 italic">Scalability</span>
                </h2>
              </div>

              <p className="text-foreground/60 text-base md:text-lg leading-relaxed max-w-md font-medium">
                บริษัท โฟลด้า จำกัด เราออกแบบสถาปัตยกรรมซอฟต์แวร์ที่ยืดหยุ่น ปลอดภัย 
                และพร้อมรองรับการขยายตัวในวันที่ธุรกิจคุณเติบโตเป็นร้อยเท่า
              </p>

              <div className="grid grid-cols-3 gap-4 pt-6">
                {[
                  { label: "Fast", sub: "Optimized" },
                  { label: "Secure", sub: "Encrypted" },
                  { label: "Stable", sub: "Redundant" }
                ].map((stat, i) => (
                  <div key={i} className="p-4 rounded-2xl bg-foreground/[0.06] border border-foreground/5 text-center group hover:border-fd-500/30 transition-colors">
                    <div className="text-fd-500 font-black text-lg md:text-xl mb-1 group-hover:scale-110 transition-transform">
                      {stat.label}
                    </div>
                    <div className="text-[12px] text-foreground/40 uppercase tracking-widest font-bold">
                      {stat.sub}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </motion.div>

        </div>
      </section>

      <section className="relative py-20 px-6 md:px-12 max-w-7xl mx-auto overflow-hidden -mt-1 border-none outline-none">
        <div className="text-center mb-16 md:mb-24 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="space-y-4"
          >
            <h2 className="text-fd-500 font-bold mb-4 uppercase tracking-[0.3em] text-[14px] italic block">
              The Folda Way
            </h2>
            <h3 className="text-4xl md:text-6xl font-bold italic tracking-tighter text-foreground uppercase leading-none">
              กระบวนการที่ <span className="text-fd-500">แม่นยำ</span>
            </h3>
          </motion.div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 relative z-10">
          {steps.map((step, index) => (
            <motion.div
              key={step.id}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.15, duration: 0.6 }}
              className="group relative p-8 rounded-[2rem] bg-secondary/30 border border-border hover:border-fd-500/30 transition-all duration-500"
            >
              <div className="absolute -top-1 right-2 text-fd-500/20 text-6xl font-black italic pointer-events-none">
                {step.id}
              </div>

              <div className="relative z-10">
                <div className="w-8 h-1 bg-fd-500 mb-6 rounded-full group-hover:w-12 transition-all" />
                <h4 className="text-lg font-bold uppercase text-foreground mb-3">
                  {step.title}
                </h4>
                <p className="text-foreground/60 md:text-lg leading-relaxed">
                  {step.desc}
                </p>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      <section className="py-16 opacity-60">
        <div className="max-w-6xl mx-auto px-6">
          <p className="text-center text-[14px] uppercase tracking-[0.4em] mb-12 font-bold text-foreground/50">
            Engineered with Modern Industry Standards
          </p>
          <div className="flex flex-wrap justify-center gap-10 md:gap-16 items-center grayscale">
            <span className="text-xl md:text-1xl font-bold">ANGULAR</span>
            <span className="text-xl md:text-1xl font-bold">REACT</span>
            <span className="text-xl md:text-1xl font-bold">NEXT.JS</span>
            <span className="text-xl md:text-1xl font-bold">TYPESCRIPT</span>
            <span className="text-xl md:text-1xl font-bold">POSTGRESQL</span>
            <span className="text-xl md:text-1xl font-bold">TAILWIND</span>
            <span className="text-xl md:text-1xl font-bold">DOCKER</span>
          </div>
        </div>
      </section>


    </div>
  );
}